import React, { useState, useEffect } from "react";
import {
  TextField, Select, MenuItem, FormControl, RadioGroup, FormControlLabel, Radio,
  Checkbox, Typography, Card, Button, Box, Grid, CircularProgress, IconButton, InputLabel
} from "@mui/material";
import ThreeWheelerIcon from "@mui/icons-material/ElectricRickshaw";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import DeleteIcon from '@mui/icons-material/Delete';

const BUS_CONFIG = [
  { label: "Single Deck: Up to 18", value: 18, liabilityBase: 2500, odBasic: 2814, odRate: 0.01 },
  { label: "Single Deck: 19–24", value: 24, liabilityBase: 2625, odBasic: 3072, odRate: 0.01 },
  { label: "Single Deck: 25–30", value: 30, liabilityBase: 2750, odBasic: 3612, odRate: 0.01 },
  { label: "Single Deck: 31–36", value: 36, liabilityBase: 3000, odBasic: 4124, odRate: 0.01 },
  { label: "Single Deck: Over 36", value: 37, liabilityBase: 3250, odBasic: 4410, odRate: 0.01 },
  { label: "Double Deck: Up to 60", value: 60, liabilityBase: 3250, odBasic: 4410, odRate: 0.019 },
  { label: "Double Deck: Over 60", value: 61, liabilityBase: 3750, odBasic: 5460, odRate: 0.019 },
];

const vehicleOptions = [
  { value: "threeWheeler", label: "Three Wheeler", icon: <ThreeWheelerIcon />, color: "#2e7d32" },
  { value: "truck", label: "Truck", icon: <LocalShippingIcon />, color: "#1976d2" },
  { value: "taxi", label: "Taxi", icon: <LocalTaxiIcon />, color: "#9c27b0" },
  { value: "bus", label: "Bus", icon: <DirectionsBusIcon />, color: "#ed6c02" }
];

const MotorLiabilityForm = ({ onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vehicleType: "bus",
    busSeats: 18,
    fiv: "",
    ncb: 0,
    riskOptions: { flood: false, earthquake: false, riot: false, avls: true },
    passengers: 18,
    staffCount: 1,
    vehicleNumber: "",
    ownerName: "",
    engineNumber: "",
    chassisNumber: "",
    cc_capacity: "",
    vehicle_class: "",
    mfgYear: "",
    isScanned: false
  });

  const [previews, setPreviews] = useState({ registrationDoc: null, previousInsuranceDoc: null });

  const formatLakhCrore = (val) => {
    if (!val) return "";
    const number = parseFloat(val.toString().replace(/,/g, ""));
    return isNaN(number) ? "" : number.toLocaleString("en-IN");
  };

  useEffect(() => {
    if (formData.vehicleType === "bus") {
      setFormData(prev => ({ ...prev, passengers: prev.busSeats }));
    }
  }, [formData.busSeats, formData.vehicleType]);

  const calculatePremium = (data) => {
    const fiv = parseFloat(data.fiv?.toString().replace(/,/g, "")) || 0;
    const config = BUS_CONFIG.find(c => c.value === parseInt(data.busSeats)) || BUS_CONFIG[0];
    
    const BASIC_PREMIUM = config.odBasic;
    const OD_VAL = fiv * config.odRate;
    const RIOT_COST = data.riskOptions.riot ? fiv * 0.005 : 0;
    const FLOOD_COST = data.riskOptions.flood ? fiv * 0.0025 : 0;
    const EQ_COST = data.riskOptions.earthquake ? fiv * 0.0025 : 0;

    const avlsBase = BASIC_PREMIUM + OD_VAL + RIOT_COST + FLOOD_COST + EQ_COST;
    const AVLS = data.riskOptions.avls ? avlsBase * 0.10 : 0;
    const OD_AFTER_AVLS = avlsBase - AVLS;

    const NCB_LESS = OD_AFTER_AVLS * (parseFloat(data.ncb) / 100);
    const SUB_TOTAL_A = OD_AFTER_AVLS - NCB_LESS;

    const MOTOR_LIABILITY = config.liabilityBase;
    const PASSENGER_COST = (parseInt(data.passengers) || 0) * 225; 
    const STAFF_COST = (parseInt(data.staffCount) || 0) * 150;
    const DRIVER_COST = 150; 

    const NET_PREMIUM = SUB_TOTAL_A + MOTOR_LIABILITY + DRIVER_COST + STAFF_COST + PASSENGER_COST;
    const VAT_AMOUNT = NET_PREMIUM * 0.15;
    const GROSS_PREMIUM = Math.round(NET_PREMIUM + VAT_AMOUNT);

    return {
      BASIC_PREMIUM, OD_VAL, RIOT_COST, FLOOD_COST, EQ_COST, AVLS,OD_AFTER_AVLS,
      NCB_LESS, SUB_TOTAL_A, MOTOR_LIABILITY, STAFF_COST,
      PASSENGER_COST, DRIVER_COST, NET_PREMIUM, VAT_AMOUNT, GROSS_PREMIUM,
      odRate: config.odRate
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const calc = calculatePremium(formData);
    onSubmit({
      productType: "Commercial Vehicle Insurance",
      data: { ...formData, ...calc },
    });
  };

  const handleRegistrationScan = async (file) => {
    setLoading(true);
    const uploadData = new FormData();
    uploadData.append("registrationDoc", file);
    try {
      //const response = await fetch("http://localhost/bgic-store/admin/scan_registrationdoc.php", {
        const response = await fetch("https://www.bgicdigital.com/admin/scan_registrationdoc.php", {
        method: "POST",
        body: uploadData,
      });
      const result = await response.json();
      if (result.success) {
        setFormData(prev => ({
          ...prev,
          vehicleNumber: result.data.reg_no || "",
          ownerName: result.data.owner_name || "",
          engineNumber: result.data.engine_no || "",
          chassisNumber: result.data.chassis_no || "",
          cc_capacity: result.data.cc_capacity || "",
          vehicle_class: result.data.vehicle_class || "",
          mfgYear: result.data.mfg_year || "",
          isScanned: true
        }));
      }
    } catch (e) { console.error("Scan Error", e); } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData(prev => ({ ...prev, riskOptions: { ...prev.riskOptions, [name]: checked } }));
    } else if (name === "fiv") {
      setFormData(prev => ({ ...prev, [name]: value.replace(/[^0-9]/g, "") }));
    } else if (files) {
      const file = files[0];
      if (file) {
        setPreviews(p => ({ ...p, [name]: URL.createObjectURL(file) }));
        setFormData(p => ({ ...p, [name]: file }));
        if (name === "registrationDoc") handleRegistrationScan(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const removeFile = (name) => {
    setFormData(prev => ({ ...prev, [name]: null, ...(name === 'registrationDoc' && { isScanned: false }) }));
    if (previews[name]) { URL.revokeObjectURL(previews[name]); setPreviews(p => ({ ...p, [name]: null })); }
  };

  const calc = calculatePremium(formData);

  return (
    
    <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
      <form onSubmit={handleSubmit}>

 <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Commercial Vehicle Insurance
                  </Typography>
              
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    Please complete the form below to proceed.
                  </Typography>
                </Box>
              
                {/*<Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => setOpenDetails(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 500,
                  }}
                >
                  View Details
                </Button> */}
              </Box>




        <Grid container spacing={3}>
          
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {vehicleOptions.map((opt) => (
                <Grid item xs={3} key={opt.value}>
                  <Card onClick={() => setFormData(p => ({ ...p, vehicleType: opt.value }))}
                    sx={{ cursor: 'pointer', textAlign: 'center', p: 2, border: formData.vehicleType === opt.value ? `2px solid ${opt.color}` : "1px solid #eee", boxShadow: 'none' }}>
                    {React.cloneElement(opt.icon, { sx: { fontSize: 32, color: opt.color } })}
                    <Typography variant="body1" sx={{ fontWeight: 700, mt: 1 }}>{opt.label}</Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {formData.vehicleType === "bus" ? (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ed6c02', mb: 1 }}>Capacity Configuration</Typography>
                <RadioGroup row name="busSeats" value={formData.busSeats} onChange={handleChange}>
                  {BUS_CONFIG.map(c => <FormControlLabel key={c.value} value={c.value} control={<Radio color="warning" />} label={<Typography sx={{ fontSize: '1.1rem' }}>{c.label}</Typography>} />)}
                </RadioGroup>
              </Grid>

              <Grid item xs={12} md={4}><TextField label="Vehicle FIV (Tk.)" name="fiv" value={formData.fiv} onChange={handleChange} helperText={<Typography variant="body2" sx={{fontWeight: 700}}>{formatLakhCrore(formData.fiv)}</Typography>} fullWidth required sx={{input: { fontSize: '1.1rem' }}}/></Grid>
              <Grid item xs={6} md={2}><TextField label="Passengers" type="number" name="passengers" value={formData.passengers} onChange={handleChange} fullWidth required/></Grid>
              <Grid item xs={6} md={2}><TextField label="Staff" type="number" name="staffCount" value={formData.staffCount} onChange={handleChange} fullWidth required/></Grid>
              
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>NCB Discount %</InputLabel>
                  <Select name="ncb" value={formData.ncb} onChange={handleChange} label="NCB Discount %">
                    {[0, 30, 40, 50].map(v => <MenuItem key={v} value={v}>{v}%</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <FormControlLabel control={<Checkbox name="avls" checked={formData.riskOptions.avls} onChange={handleChange} />} label="AVLS (10%)" />
                  <FormControlLabel control={<Checkbox name="riot" checked={formData.riskOptions.riot} onChange={handleChange} />} label="Riot & Strike (0.5%)" />
                  <FormControlLabel control={<Checkbox name="flood" checked={formData.riskOptions.flood} onChange={handleChange} />} label="Flood (0.25%)" />
                  <FormControlLabel control={<Checkbox name="earthquake" checked={formData.riskOptions.earthquake} onChange={handleChange} />} label="Earthquake (0.25%)" />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mt: 2, borderRadius: 0 }}>
                  <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "15px", "& td": { border: "1px solid #999", px: 2, py: 1 } }}>
                    <thead>
                      <tr style={{ background: "#d9d9d9" }}><th colSpan="4" style={{ padding: "10px", fontWeight: 700, border: "1px solid #999" }}>PREMIUM COMPUTATION (COMMERCIAL VEHICLE)</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Basic TP (Tariff)</td><td width="40">Tk.</td><td align="right">{calc.BASIC_PREMIUM.toFixed(2)}</td><td></td></tr>
                      <tr><td>Own Damage (OD) Premium ({(calc.odRate * 100).toFixed(2)}% on FIV)</td><td>Tk.</td><td align="right">{calc.OD_VAL.toFixed(2)}</td><td></td></tr>
                      {calc.RIOT_COST > 0 && <tr><td>&nbsp;&nbsp;Riot & Strike (0.50% on FIV)</td><td>Tk.</td><td align="right">{calc.RIOT_COST.toFixed(2)}</td><td></td></tr>}
                      {calc.FLOOD_COST > 0 && <tr><td>&nbsp;&nbsp;Flood (0.25% on FIV)</td><td>Tk.</td><td align="right">{calc.FLOOD_COST.toFixed(2)}</td><td></td></tr>}
                      {calc.EQ_COST > 0 && <tr><td>&nbsp;&nbsp;Earthquake (0.25% on FIV)</td><td>Tk.</td><td align="right">{calc.EQ_COST.toFixed(2)}</td><td></td></tr>}
                      {calc.AVLS > 0 && <tr><td>AVLS Discount (10% on above sum)</td><td>Tk.</td><td align="right" style={{ color: 'red' }}>-{calc.AVLS.toFixed(2)}</td><td></td></tr>}
                      {calc.NCB_LESS > 0 && <tr><td>Less: NCB Discount ({formData.ncb}%)</td><td>Tk.</td><td align="right" style={{ color: 'red' }}>-{calc.NCB_LESS.toFixed(2)}</td><td></td></tr>}
                      <tr style={{ fontWeight: 'bold', background: '#f5f5f5' }}><td>Sub Total (Section A)</td><td>Tk.</td><td align="right">{calc.SUB_TOTAL_A.toFixed(2)}</td><td></td></tr>
                      <tr><td>Motor Liability (Third Party)</td><td>Tk.</td><td></td><td align="right">{calc.MOTOR_LIABILITY.toFixed(2)}</td></tr>
                      <tr><td>Passenger Liability ({formData.passengers} persons @ 225/-)</td><td>Tk.</td><td></td><td align="right">{calc.PASSENGER_COST.toFixed(2)}</td></tr>
                      <tr><td>Staff,Supervisor Liability ({formData.staffCount} persons @ 150/-)</td><td>Tk.</td><td></td><td align="right">{calc.STAFF_COST.toFixed(2)}</td></tr>
                      <tr><td>Paid Driver (1 person @ 150/-)</td><td>Tk.</td><td></td><td align="right">{calc.DRIVER_COST.toFixed(2)}</td></tr>
                      <tr style={{ fontWeight: 'bold' }}><td>Net Premium</td><td>Tk.</td><td></td><td align="right">{calc.NET_PREMIUM.toFixed(2)}</td></tr>
                      <tr><td>Add: VAT (15%)</td><td>Tk.</td><td></td><td align="right">{calc.VAT_AMOUNT.toFixed(2)}</td></tr>
                      <tr style={{ fontWeight: "bold", background: "#eee", fontSize: '1.2rem' }}><td>Total Gross Premium (Payable)</td><td>Tk.</td><td></td><td align="right">Tk. {calc.GROSS_PREMIUM.toLocaleString()}</td></tr>
                    </tbody>
                  </Box>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ color: "red", fontWeight: "bold", mb: 1 }}>Registration Scan *</Typography>
                {!previews.registrationDoc ? (
                  <input type="file" name="registrationDoc" onChange={handleChange} required />
                ) : (
                  <Box sx={{ position: 'relative', border: '1px solid red', p: 1, bgcolor: '#fff5f5' }}>
                    <img src={previews.registrationDoc} alt="Scan" style={{ width: '100%', height: '100px', objectFit: 'contain' }} />
                    <IconButton size="small" color="error" onClick={() => removeFile('registrationDoc')} sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'white' }}><DeleteIcon /></IconButton>
                  </Box>
                )}
              </Grid>

              {formData.isScanned && (
                <Grid item xs={12}>
                    <Grid container spacing={2} sx={{ p: 2, bgcolor: '#f9f9f9', border: '1px solid #ddd' }}>
                        <Grid item xs={12} md={6}><TextField size="small" label="Reg No" name="vehicleNumber" value={formData.vehicleNumber} onChange={handleChange} fullWidth/></Grid>
                        <Grid item xs={12} md={6}><TextField size="small" label="Owner" name="ownerName" value={formData.ownerName} onChange={handleChange} fullWidth/></Grid>
                        <Grid item xs={6} md={3}><TextField size="small" label="Engine" name="engineNumber" value={formData.engineNumber} onChange={handleChange} fullWidth/></Grid>
                        <Grid item xs={6} md={3}><TextField size="small" label="Chassis" name="chassisNumber" value={formData.chassisNumber} onChange={handleChange} fullWidth/></Grid>
                        <Grid item xs={6} md={3}><TextField size="small" label="Class/CC" name="vehicle_class" value={formData.vehicle_class} onChange={handleChange} fullWidth/></Grid>
                        <Grid item xs={6} md={3}><TextField size="small" label="Year" name="mfgYear" value={formData.mfgYear} onChange={handleChange} fullWidth/></Grid>
                    </Grid>
                </Grid>
              )}

              <Grid item xs={12}><Button type="submit" variant="contained" fullWidth sx={{ mt: 2, py: 2, fontSize: '1.1rem', fontWeight: 800 }}>ADD TO CART (TK. {calc.GROSS_PREMIUM.toLocaleString()})</Button></Grid>
            </>
          ): (
  <Grid item xs={12}>
    <Typography
      variant="h6"
      sx={{ mt: 3, fontWeight: 600, color: "red", textAlign: "center" }}
    >
      Insurance for this type of vehicle will be available soon.
    </Typography>
  </Grid>
)}
        </Grid>
      </form>
    </Box>
  );
};

export default MotorLiabilityForm;